import { Injectable } from '@angular/core';
import { IPlanInfo } from '../interfaces/plan-info.interface';
import {
    DISCOUNT_HEADER_TEXT,
    DISCOUNT_TEXT,
    REFERRER_TEXT,
    VOLUMEN_DISCOUNT_TEXT,
} from '../pages/users/list.data';

export const PLAN_RECURRENCE_MONTHLY = 1;
export const PLAN_RECURRENCE_ANNUAL = 12;

const PLAN_TYPE_DISPLAY_ORDER = ['START', 'PRO', 'BUSINESS', 'PROFESIONAL', 'PREMIUM', 'PLUS'];

export function roundToTwoDecimals(value: number | null | undefined): number {
    const num = Number(value);
    if (!Number.isFinite(num)) {
        return 0;
    }

    return Math.round((num + Number.EPSILON) * 100) / 100;
}

const RAW_PLAN_NUMERIC_FIELDS = [
    'price',
    'landing_freemium_discount',
    'landing_fremium_discount_users',
    'half_off_discount',
    'base_rfcs',
    'user_price',
];

@Injectable({
    providedIn: 'root'
})
export class PlanService {
    getPlans(rawPlans: any[], is_referer: boolean): IPlanInfo[] {
        if (!Array.isArray(rawPlans)) {
            return [];
        }

        return rawPlans.map(plan => this.assignPlanDetails(plan, is_referer));
    }

    assignPlanDetails(plan: any, is_referer: boolean): IPlanInfo {
        const label = plan?.landing_freemium_label || '';
        const planShortName = this.getPlanShortNameFromRaw(plan);
        const recurrence = Number(plan?.recurrence);
        const discountTextKey = this.getDiscountTextKey(planShortName, recurrence, label);

        return {
            id: plan?.id,
            recurrence,
            name: label,
            price: roundToTwoDecimals(plan?.price),
            user_price: plan?.user_price != null
                ? roundToTwoDecimals(plan?.user_price)
                : roundToTwoDecimals(plan?.price),
            plan_short_name: planShortName,
            welcome_discount_text: plan?.welcome_discount_text || DISCOUNT_TEXT[discountTextKey] || '',
            header_discount_text: plan?.header_discount_text || DISCOUNT_HEADER_TEXT[discountTextKey] || this.getHeaderDiscountTextByRecurrence(recurrence),
            volumen_discount_text: plan?.volumen_discount_text || VOLUMEN_DISCOUNT_TEXT[discountTextKey] || '',
            discount_referres_text: plan?.discount_referres_text || REFERRER_TEXT[discountTextKey] || '',
            welcome_discount: 0,
            welcome_discount_percentage: roundToTwoDecimals(plan?.landing_freemium_discount),
            annual_discount_percentage: recurrence === PLAN_RECURRENCE_ANNUAL
                ? roundToTwoDecimals(plan?.landing_freemium_discount)
                : 0,
            discount_users: plan?.landing_fremium_discount_users != null
                ? roundToTwoDecimals(plan?.landing_fremium_discount_users)
                : plan?.landing_fremium_discount_users,
            landing_freemium_default_plan: plan?.landing_freemium_default_plan === true,
            number_of_users: 1,
            volumen_discount_percentage: 0,
            subtotal: 0,
            total_sale: 0,
            half_off_discount: plan?.half_off_discount != null
                ? roundToTwoDecimals(plan?.half_off_discount)
                : 0,
            discount: 0,
            discount_deal: 0,
            is_referer,
            base_rfcs: plan?.base_rfcs != null ? roundToTwoDecimals(plan?.base_rfcs) : undefined,
        };
    }

    private getDiscountTextKey(planShortName: string, recurrence: number, label: string): string {
        if (DISCOUNT_HEADER_TEXT[label] || DISCOUNT_TEXT[label]) {
            return label;
        }

        const shortName = (planShortName || '').toUpperCase();
        if (!shortName) return label;

        const paymentLabel = recurrence === PLAN_RECURRENCE_ANNUAL ? 'ANUAL' : 'MENSUAL';
        return `${shortName} ${paymentLabel}`;
    }

    private getHeaderDiscountTextByRecurrence(recurrence: number): string {
        if (recurrence === PLAN_RECURRENCE_ANNUAL) {
            return 'En pago anual 25% de descuento';
        }

        if (recurrence === PLAN_RECURRENCE_MONTHLY) {
            return '50% de descuento en tus primeros 3 meses. En pago anual 25% de descuento';
        }

        return '';
    }

    normalizeRawPlans(result: any): any[] {
        const plans = Array.isArray(result) ? result : (result?.data || result?.plans || []);
        return plans.map(plan => this.normalizeRawPlan(plan));
    }

    normalizeRawPlan(plan: any): any {
        if (!plan || typeof plan !== 'object') {
            return plan;
        }

        const normalizedPlan = { ...plan };

        RAW_PLAN_NUMERIC_FIELDS.forEach(field => {
            if (normalizedPlan[field] != null && normalizedPlan[field] !== '') {
                normalizedPlan[field] = roundToTwoDecimals(normalizedPlan[field]);
            }
        });

        return normalizedPlan;
    }

    getPlanShortNameFromRaw(plan: any): string {
        const label = plan?.landing_freemium_label || plan?.name || '';
        const shortName = (label.split(' ')[0] || '').toUpperCase();

        if (shortName === 'BUSINESS') {
            return 'PRO';
        }

        if (shortName === 'STARTER') {
            return 'START';
        }

        return shortName || label.split(' ')[0] || '';
    }

    getUniquePlanTypes(plans: IPlanInfo[]): string[] {
        const types = new Set<string>();

        plans.forEach(plan => {
            if (plan?.plan_short_name) {
                types.add(plan.plan_short_name.toUpperCase());
            }
        });

        return Array.from(types).sort((a, b) => {
            const indexA = PLAN_TYPE_DISPLAY_ORDER.indexOf(a);
            const indexB = PLAN_TYPE_DISPLAY_ORDER.indexOf(b);
            const orderA = indexA === -1 ? PLAN_TYPE_DISPLAY_ORDER.length : indexA;
            const orderB = indexB === -1 ? PLAN_TYPE_DISPLAY_ORDER.length : indexB;

            return orderA - orderB;
        });
    }

    getAvailableRecurrences(plans: IPlanInfo[], planShortName: string): number[] {
        return plans
            .filter(plan => (plan.plan_short_name || '').toUpperCase() === planShortName.toUpperCase())
            .map(plan => Number(plan.recurrence))
            .filter(recurrence => recurrence === PLAN_RECURRENCE_MONTHLY || recurrence === PLAN_RECURRENCE_ANNUAL)
            .sort((a, b) => a - b);
    }

    findPlanByTypeAndRecurrence(
        plans: IPlanInfo[],
        planShortName: string,
        recurrence: number
    ): IPlanInfo | undefined {
        return plans.find(plan =>
            (plan.plan_short_name || '').toUpperCase() === planShortName.toUpperCase()
            && Number(plan.recurrence) === Number(recurrence)
        );
    }

    getDefaultPlan(plans: IPlanInfo[]): IPlanInfo | undefined {
        return plans.find(plan => plan.landing_freemium_default_plan) || plans[0];
    }

    resolveInitialPlan(plans: IPlanInfo[]): IPlanInfo | undefined {
        const defaultPlan = this.getDefaultPlan(plans);
        if (!defaultPlan?.plan_short_name) {
            return defaultPlan;
        }

        return this.findPlanByTypeAndRecurrence(
            plans,
            defaultPlan.plan_short_name,
            PLAN_RECURRENCE_MONTHLY
        ) || defaultPlan;
    }

    getRecurrenceLabel(recurrence: number): string {
        return Number(recurrence) === PLAN_RECURRENCE_ANNUAL ? 'Anual' : 'Mensual';
    }

    formatPlanTypeLabel(planShortName: string): string {
        if (!planShortName) return '';
        return planShortName.charAt(0).toUpperCase() + planShortName.slice(1).toLowerCase();
    }
}
